( () => {

	// Select all dropdown elements
	const globalDropdownElements = Array.from(
		document.querySelectorAll<HTMLDivElement>(
			'div[data-ufr-component="filter-dropdown"]',
		),
	);

	/**
	 * Factory function to generate a global click handler for a dropdown element.
	 *
	 * The click handler gets attached to the body element as soon as a dropdown
	 * is opened.
	 * This handler then closes the dropdown if the click target is not
	 * on the dropdown or its contents.
	 *
	 * @param dropdownElement      The dropdown element the handler should be
	 *                             generated for.
	 * @param contentElement       The content element that gets updated inside
	 *                             the dropdown.
	 * @param closeDropdownHandler The function that handles the closing of the
	 *                             dropdown itself.
	 *
	 * @return                     The generated click handler function
	 */
	const generateGlobalClickHandler = (
		dropdownElement: typeof globalDropdownElements[number],
		contentElement: unknown,
		closeDropdownHandler: ( dropdownElement: typeof globalDropdownElements[number], contentElement: unknown ) => void,
	) => {
		return ( ( event: MouseEvent ) => {
			if( ! ( event.target instanceof Element ) ) return;
			if( event.target === dropdownElement || dropdownElement.contains( event.target ) ) return;
			closeDropdownHandler( dropdownElement, contentElement );
		} );
	};

	// Iterate through all dropdown elements and attach their click handler
	globalDropdownElements.forEach( dropdownElement => {
		const toggleButton = dropdownElement.querySelector<HTMLButtonElement>(
			'button[data-ufr-role="dropdown-toggle-button"]',
		);
		const dropdownContent = dropdownElement.querySelector<HTMLDivElement>(
			'div[data-ufr-role="dropdown-content-container"]',
		);

		/**
		 * Open a dropdown element.
		 * The opening itself is done solely by setting the aria-expanded attribute.
		 * Afterwards, a click handler is attached to the body element to handle
		 * closing the dropdown if the user clicks outside of it.
		 *
		 * @param element        The dropdown element to get opened.
		 * @param contentElement The content element is a static element inside a
		 *                       dropdown that gets its 'inert' attribute removed.
		 */
		const openDropdown = (
			element: typeof globalDropdownElements[number],
			contentElement: typeof dropdownContent,
		) => {
			element.setAttribute( 'aria-expanded', 'true' );
			contentElement.removeAttribute( 'inert' );
			document.body.addEventListener( 'click', globalClickHandlerCallback );
		};

		/**
		 * Close a dropdown element.
		 * The closing itself is done solely by setting the aria-expanded attribute.
		 * Afterwards, the click handler that got registered when opening the
		 * dropdown gets unregistered again.
		 *
		 * @param element        The dropdown element to get opened.
		 * @param contentElement The content element is a static element inside a
		 *                       dropdown that gets added a 'inert' attribute.
		 */
		const closeDropdown = (
			element: typeof globalDropdownElements[number],
			contentElement: typeof dropdownContent,
		) => {
			element.setAttribute( 'aria-expanded', 'false' );
			contentElement.setAttribute( 'inert', '1' );
			document.body.removeEventListener( 'click', globalClickHandlerCallback );
		};

		// Generate a constant click handler function that is persisted inside
		// the current closure for the entirety of the page session.
		// This is required so the function pointer is the same when attaching
		// and removing the click handler for this dropdown.
		const globalClickHandlerCallback = generateGlobalClickHandler( dropdownElement, dropdownContent, closeDropdown );


		// The event listener itself is attached to the toggle button that opens
		// the dropdown.
		toggleButton.addEventListener( "click", () =>
			'true' === dropdownElement.ariaExpanded
				? closeDropdown( dropdownElement, dropdownContent )
				: openDropdown( dropdownElement, dropdownContent ),
		);

	} );


} )();
